import React, { useEffect, useReducer, memo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ValidationError } from 'yup'
import { ICoverRequestData } from 'apps/booking/types'
import { getCarrierInfo } from 'apps/booking/utils/actions/carrier'
import { selectCarrierProfileInfo } from 'apps/booking/utils/selectors/carrier'
import { selectShipmentDetails } from 'apps/booking/utils/selectors/shipment'
import { requiredFields, rateCheckSchema, phoneSchema } from 'apps/booking/utils/validationSchema'
import { TextInput } from 'components/atoms'
import formatPhone from 'utils/formatPhone'
import BookingInfo, { DispatcherSelect, PhoneEntry } from './BookingInfo'
import CarrierInfo, { CarrierSelect, IAutocomplete, CarrierRate } from './CarrierInfo'

const fieldsReducer = (
  state: ICoverRequestData,
  { field, value }: { field: string; value: string | boolean }
) => {
  return {
    ...state,
    [field]: value,
  }
}

interface ICarrierEntryForm {
  setCoverFields: (fields?: ICoverRequestData) => void
}

const defaultCoverFields = {
  amount: '',
  carrierApplicationId: '',
  carrierContactUserId: '',
  carrierContactName: '',
  carrierContactPhone: '',
  carrierContactPhoneExt: '',
}

type GenericObject = {
  [key: string]: string
}

const checkRequiredFieldValues = (requiredField: string, formFields: GenericObject): boolean =>
  Boolean(formFields[requiredField])

export interface ValidateReducer {
  field: string
  error: string
}

const validateReducer = (state: GenericObject, { error, field }: ValidateReducer) => ({
  ...state,
  [field]: error,
})

const CarrierEntryForm = memo<ICarrierEntryForm>(({ setCoverFields }) => {
  const dispatch = useDispatch()
  const { contactPhone } = useSelector(selectCarrierProfileInfo)
  const { id: shipmentId, shipperRate = '' } = useSelector(selectShipmentDetails)

  const [fields, dispatchFields] = useReducer(fieldsReducer, defaultCoverFields)
  const [errors, dispatchErrors] = useReducer(validateReducer, defaultCoverFields)
  const [rateError, setRateError] = useState('') // Error doesn't prevent submission

  const clearErrors = (field: string) => {
    if (errors[field]) {
      dispatchErrors({ field, error: '' })
    }
  }

  // Validate Inputs
  const validateRateInput = (event: React.FocusEvent<HTMLInputElement>) => {
    const { value } = event.target

    if (!shipperRate || !value) return

    rateCheckSchema(shipperRate)
      .validate(value)
      .then(() => setRateError(''))
      .catch((e: ValidationError) => setRateError(e.message))
  }

  const validatePhone = (phoneNumber: string) => {
    phoneSchema
      .validate(phoneNumber)
      .then(() => clearErrors('carrierContactPhone'))
      .catch((e: ValidationError) =>
        dispatchErrors({ field: 'carrierContactPhone', error: e.message })
      )
  }

  const checkRequiredValues = (formFields: GenericObject): boolean => {
    const requiredInputs = requiredFields.carrier.every((field: string) =>
      checkRequiredFieldValues(field, formFields)
    )

    return requiredInputs
  }

  useEffect(() => {
    const hasNoErrors = Object.values(errors).every((error: string) => !error)
    const isValid = checkRequiredValues(fields)

    if (isValid && hasNoErrors) {
      setCoverFields(fields)
    } else {
      setCoverFields(undefined)
    }
  }, [setCoverFields, fields, errors])

  // Prefill phone values
  useEffect(() => {
    if (contactPhone && !fields.carrierContactPhone) {
      const formattedPhone = formatPhone(contactPhone)
      dispatchFields({ field: 'carrierContactPhone', value: formattedPhone })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactPhone])

  useEffect(() => {
    if (fields.carrierContactPhone) {
      validatePhone(fields.carrierContactPhone)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields.carrierContactPhone])

  // Change Handlers
  const changeCarrierHandler = (selected: IAutocomplete) => {
    const { fullSelection: carrier } = selected
    const value = carrier?.id || ''

    dispatchFields({ field: 'carrierApplicationId', value })

    if (fields.carrierApplicationId === carrier?.id || !value) return
    dispatch(getCarrierInfo(carrier.id, shipmentId))

    // If a new carrier is selected, clear carrier contact fields
    if (fields.carrierContactUserId) {
      dispatchFields({ field: 'carrierContactUserId', value: '' })
      dispatchFields({ field: 'carrierContactName', value: '' })
      dispatchFields({ field: 'carrierContactPhone', value: '' })
    }
  }

  const changeDispatcherHandler = (selected: IAutocomplete) => {
    const { fullSelection: dispatcher } = selected
    const value = dispatcher?.id || ''

    dispatchFields({ field: 'carrierContactUserId', value })

    if (value) {
      const { name, phone } = dispatcher
      const formattedPhone = formatPhone(phone)
      dispatchFields({ field: 'carrierContactName', value: name })
      dispatchFields({ field: 'carrierContactPhone', value: formattedPhone })
    }
  }

  const changeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name: field, value } = event.target

    dispatchFields({ field, value })
  }

  return (
    <div>
      <span>Step 1 of 2</span>
      <form>
        <CarrierInfo>
          <CarrierSelect
            changeCarrierHandler={changeCarrierHandler}
            carrierId={fields?.carrierApplicationId}
          />
          <CarrierRate
            changeRateHandler={changeHandler}
            onBlurRateInput={validateRateInput}
            rateError={rateError}
          />
        </CarrierInfo>
        <BookingInfo>
          <DispatcherSelect
            changeDispatcherHandler={changeDispatcherHandler}
            dispatcherId={fields?.carrierContactUserId}
            carrierId={fields?.carrierApplicationId}
          />
          <TextInput
            required
            onChange={changeHandler}
            name='carrierContactName'
            label='Contact Name'
            value={fields?.carrierContactName}
            data-test='carrier-contact-name'
            className='mt4'
          />
          <PhoneEntry
            changePhoneHandler={changeHandler}
            changePhoneExtHandler={changeHandler}
            contactPhone={fields?.carrierContactPhone}
            contactPhoneExt={fields?.carrierContactPhoneExt}
            contactPhoneError={errors.carrierContactPhone}
          />
        </BookingInfo>
      </form>
    </div>
  )
})
export default CarrierEntryForm
